import { Link, useLocation } from "react-router-dom";
import { ReactNode, useEffect } from "react";
import { CartDrawer } from "@/components/CartDrawer";
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { apiFetch } from "@/lib/api";

// Pre-warm browser pool once per session so on-demand search is fast
let poolWarmed = false;
function warmBrowserPool() {
  if (poolWarmed) return;
  poolWarmed = true;
  apiFetch("/api/pool/warm", { method: "POST" }).catch(() => {});
}

export function Layout({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  useEffect(() => { warmBrowserPool(); }, []);
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  const navItems = [
    { to: "/", label: "Home" },
    { to: "/shop", label: "Shop" },
    { to: "/services", label: "Services" },
    { to: "/tire-size-guide", label: "Tire Size Guide" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
    { to: "/checkout", label: "Checkout" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 bg-white text-black px-3 py-2 rounded shadow z-[999]"
      >
        Skip to content
      </a>

      {/* ── Navigation ── */}
      <nav className="sticky top-0 z-50 bg-[#040C1A]/85 backdrop-blur-xl border-b border-white/[0.06] text-white">
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-6 px-4 sm:px-6 lg:px-8">

          <div className="flex flex-1 items-center gap-4">
            {/* Mobile hamburger */}
            <div className="lg:hidden">
              <Sheet>
                <SheetTrigger asChild>
                  <button
                    type="button"
                    aria-label="Open navigation menu"
                    className="inline-flex size-9 items-center justify-center rounded-lg border border-white/[0.08] bg-white/[0.04] text-white transition hover:bg-white/[0.08] active:scale-95"
                  >
                    <Menu className="h-4 w-4" />
                  </button>
                </SheetTrigger>
                <SheetContent
                  side="top"
                  className="max-h-[80vh] w-full border-none text-white shadow-2xl"
                  style={{ background: "linear-gradient(180deg, #040C1A 0%, #08172E 100%)" }}
                >
                  <SheetHeader className="items-start text-left">
                    <SheetTitle>
                      <Logo variant="mark" className="[&_img]:h-10 [&_img]:sm:h-11" />
                    </SheetTitle>
                  </SheetHeader>
                  <div className="mt-6 flex flex-col gap-1.5 text-sm font-medium">
                    {navItems.map((item) => (
                      <SheetClose asChild key={item.to}>
                        <Link
                          to={item.to}
                          className={`rounded-lg px-4 py-2.5 transition-all ${
                            pathname === item.to
                              ? "bg-[#00AADD]/15 text-[#00AADD]"
                              : "text-white/70 hover:bg-white/[0.06] hover:text-white"
                          }`}
                        >
                          {item.label}
                        </Link>
                      </SheetClose>
                    ))}
                    <SheetClose asChild>
                      <Link
                        to="/book"
                        className="mt-2 rounded-full bg-[#00AADD] px-5 py-2.5 text-center text-sm font-semibold text-white transition hover:bg-[#00AADD]/90"
                      >
                        Book Now
                      </Link>
                    </SheetClose>
                  </div>
                  <div className="mt-6 section-divider" />
                  <div className="mt-4">
                    <SheetClose asChild>
                      <a
                        href="mailto:orders@apaauto.ca"
                        className="block text-center text-xs text-white/40 hover:text-[#00AADD] transition-colors"
                      >
                        orders@apaauto.ca
                      </a>
                    </SheetClose>
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            <Link to="/" className="inline-flex items-center transition-opacity hover:opacity-90">
              <Logo variant="mark" className="[&_img]:h-11 [&_img]:translate-y-px [&_img]:sm:h-12 [&_img]:sm:-my-1" />
            </Link>
          </div>

          {/* Desktop nav — pill-style active state */}
          <div className="hidden items-center gap-1 lg:flex">
            {[
              { to: "/shop", label: "Shop" },
              { to: "/services", label: "Services" },
              { to: "/tire-size-guide", label: "Size Guide" },
              { to: "/about", label: "About" },
              { to: "/contact", label: "Contact" },
            ].map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                  pathname === to
                    ? "bg-[#00AADD]/15 text-[#00AADD]"
                    : "text-white/60 hover:text-white hover:bg-white/[0.06]"
                }`}
              >
                {label}
              </Link>
            ))}

            <Button asChild variant="automotive" size="sm" className="ml-2 rounded-full px-5 h-8 text-xs shadow-[0_0_16px_rgba(0,170,221,0.3)]">
              <Link to="/book">Book Now</Link>
            </Button>

            <div className="ml-2">
              <CartDrawer
                triggerVariant="ghost"
                triggerSize="icon"
                triggerClassName="text-white/60 hover:text-white hover:bg-white/[0.06] rounded-full h-9 w-9"
                hideLabel
                triggerAriaLabel="Open cart"
              />
            </div>
          </div>

          <div className="flex items-center lg:hidden">
            <CartDrawer
              triggerVariant="ghost"
              triggerSize="icon"
              triggerClassName="text-white/60 hover:text-white hover:bg-white/[0.06] rounded-full h-9 w-9"
              hideLabel
              triggerAriaLabel="Open cart"
            />
          </div>
        </div>
      </nav>

      <div id="main-content">{children}</div>

      {/* ── Footer ── */}
      <footer className="dark-surface pt-14 pb-8">
        <div className="section-divider" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-[1.8fr_1fr_1fr_1fr]">
            <div className="space-y-4">
              <Link to="/" className="inline-flex"><Logo /></Link>
              <p className="text-sm leading-6 text-white/45 max-w-xs">
                Full-service automotive care — tires, brakes, engine work, and more — from our Milton facility. All makes and models welcome.
              </p>
              <p className="text-[11px] text-white/25 tracking-widest uppercase">APAAuto.ca</p>
            </div>

            <div className="space-y-4">
              <h4 className="text-xs font-bold uppercase tracking-wider text-[#00AADD]/70">Quick Links</h4>
              <ul className="space-y-2 text-sm text-white/50">
                <li><Link to="/services" className="hover:text-white transition-colors">Services</Link></li>
                <li><Link to="/shop" className="hover:text-white transition-colors">Shop Tires</Link></li>
                <li><Link to="/book" className="hover:text-white transition-colors">Book Appointment</Link></li>
                <li><Link to="/delivery" className="hover:text-white transition-colors">Delivery Info</Link></li>
                <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
              </ul>
            </div>

            <div className="space-y-4">
              <h4 className="text-xs font-bold uppercase tracking-wider text-[#00AADD]/70">Help</h4>
              <ul className="space-y-2 text-sm text-white/50">
                <li><Link to="/tire-size-guide" className="hover:text-white transition-colors">Find my tire size</Link></li>
                <li><Link to="/services" className="hover:text-white transition-colors">Service menu</Link></li>
                <li><Link to="/delivery" className="hover:text-white transition-colors">Delivery coverage</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact us</Link></li>
              </ul>
            </div>

            <div className="space-y-4">
              <h4 className="text-xs font-bold uppercase tracking-wider text-[#00AADD]/70">Contact</h4>
              <address className="not-italic space-y-2 text-sm text-white/50">
                <a className="block text-xs hover:text-[#00AADD] transition-colors" href="mailto:orders@apaauto.ca">orders@apaauto.ca</a>
                <a className="block text-xs hover:text-white transition-colors" href="tel:2898516911">(289) 851-6911</a>
                <a
                  className="block hover:text-white transition-colors leading-5"
                  href="https://www.google.com/maps/search/?api=1&query=342+Bronte+Street+South,+Milton,+ON"
                  target="_blank" rel="noopener noreferrer"
                >
                  342 Bronte Street South<br />Milton, ON
                </a>
                <p className="text-white/30 text-[11px]">Mon–Fri 8–5 · Sat 10–4</p>
              </address>
            </div>
          </div>

          <div className="mt-14 section-divider" />
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] text-white/25 tracking-wide">
            <p>&copy; {new Date().getFullYear()} APA Automotive</p>
            <p>APAAuto.ca · Milton, Ontario</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
